/**
 * Analyzes draft completeness for besluit documents
 */

export interface CompletenessResult {
  /** Overall completeness percentage (0-100) */
  percentage: number;
  /** Completeness level for visual display (0-4) */
  level: 0 | 1 | 2 | 3 | 4;
  /** Sections that are present */
  presentSections: string[];
  /** Sections that are missing */
  missingSections: string[];
  /** Number of (suggestie) markers found */
  suggestionCount: number;
  /** Number of [placeholder] markers found */
  placeholderCount: number;
  /** Human-readable status message */
  message: string;
}

/** Required sections for a complete besluit */
const REQUIRED_SECTIONS = [
  { id: "title", label: "Titel", pattern: /^#\s+Besluit\s+.+/m },
  {
    id: "besluitvormer",
    label: "Besluitvormer",
    pattern: /###?\s*Besluitvormer[\s\S]*?(?=###?|\n##|$)/i,
  },
  { id: "aanleiding", label: "Aanleiding", pattern: /##\s*Aanleiding[\s\S]*?(?=##|$)/i },
  { id: "overwegingen", label: "Overwegingen", pattern: /##\s*Overwegingen[\s\S]*?-\s*.+/i },
  { id: "besluit", label: "Besluit", pattern: /##\s*Besluit[\s\S]*?-\s*.+/i },
];

/** Patterns that indicate incomplete content */
const SUGGESTION_PATTERN = /\(suggestie\)/gi;
const PLACEHOLDER_PATTERN = /\[([^\]]+)\]/g;

/** Placeholders that are OK (not incomplete markers) */
const ALLOWED_PLACEHOLDERS = ["x", "X", "✓", "✗"];

/**
 * Analyze a draft besluit markdown for completeness
 */
export function analyzeDraftCompleteness(markdown: string): CompletenessResult {
  if (!markdown || markdown.trim() === "") {
    return {
      percentage: 0,
      level: 0,
      presentSections: [],
      missingSections: REQUIRED_SECTIONS.map((s) => s.label),
      suggestionCount: 0,
      placeholderCount: 0,
      message: "Nog geen concept",
    };
  }

  // Check which sections are present
  const presentSections: string[] = [];
  const missingSections: string[] = [];

  for (const section of REQUIRED_SECTIONS) {
    if (section.pattern.test(markdown)) {
      presentSections.push(section.label);
    } else {
      missingSections.push(section.label);
    }
  }

  // Count suggestions
  const suggestionMatches = markdown.match(SUGGESTION_PATTERN);
  const suggestionCount = suggestionMatches ? suggestionMatches.length : 0;

  // Count placeholders (excluding allowed ones like [x] for checkboxes)
  let placeholderCount = 0;
  let match;
  while ((match = PLACEHOLDER_PATTERN.exec(markdown)) !== null) {
    const content = match[1]?.trim() ?? "";
    if (!ALLOWED_PLACEHOLDERS.includes(content)) {
      placeholderCount++;
    }
  }
  // Reset lastIndex for potential reuse of the global regex
  PLACEHOLDER_PATTERN.lastIndex = 0;

  // Calculate base percentage from sections (each section is worth 20%)
  const sectionPercentage = (presentSections.length / REQUIRED_SECTIONS.length) * 100;

  // Apply penalties for suggestions and placeholders
  // Each suggestion/placeholder reduces max achievable score
  const incompletePenalty = (suggestionCount + placeholderCount) * 5; // 5% per item

  // Final percentage: section score minus penalties, but never below section contribution
  // If there are ANY suggestions or placeholders, cap at 90%
  let percentage = Math.max(0, sectionPercentage - incompletePenalty);

  if ((suggestionCount > 0 || placeholderCount > 0) && percentage > 90) {
    percentage = 90;
  }

  // Determine level (0-4) based on percentage
  let level: 0 | 1 | 2 | 3 | 4;
  if (percentage >= 100) {
    level = 4;
  } else if (percentage >= 75) {
    level = 3;
  } else if (percentage >= 50) {
    level = 2;
  } else if (percentage >= 25) {
    level = 1;
  } else {
    level = 0;
  }

  // Generate message
  let message: string;
  if (percentage >= 100) {
    message = "Besluit compleet!";
  } else if (missingSections.length > 0 && suggestionCount === 0 && placeholderCount === 0) {
    message = `Mist: ${missingSections.join(", ")}`;
  } else if (missingSections.length === 0 && (suggestionCount > 0 || placeholderCount > 0)) {
    const items = [];
    if (suggestionCount > 0)
      items.push(`${suggestionCount} suggestie${suggestionCount > 1 ? "s" : ""}`);
    if (placeholderCount > 0)
      items.push(`${placeholderCount} placeholder${placeholderCount > 1 ? "s" : ""}`);
    message = `Nog ${items.join(" en ")} in te vullen`;
  } else {
    const items = [];
    if (missingSections.length > 0) items.push(`mist ${missingSections.join(", ")}`);
    if (suggestionCount > 0)
      items.push(`${suggestionCount} suggestie${suggestionCount > 1 ? "s" : ""}`);
    if (placeholderCount > 0)
      items.push(`${placeholderCount} placeholder${placeholderCount > 1 ? "s" : ""}`);
    message = items.join(", ");
  }

  return {
    percentage: Math.round(percentage),
    level,
    presentSections,
    missingSections,
    suggestionCount,
    placeholderCount,
    message,
  };
}
